import { useState, useEffect } from 'react';
import { Link } from '@inertiajs/react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/cn';
import { Button, Card, ThemeToggle } from '../../../components/ui';
import {
  ThinkingIndicator,
  FeedbackPanel,
  ScoreCircle,
} from '../../../components/feedback';

export default function PracticeFeedback({
  student,
  session,
  responses,
  overall_score,
  status_counts = { correct: 0, incorrect: 0, pending: 0 },
  has_practice_exercises,
}) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [revealedCount, setRevealedCount] = useState(0);

  // Simulate AI processing delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  // Reveal feedback panels one by one
  useEffect(() => {
    if (!isLoading && revealedCount < responses.length) {
      const timer = setTimeout(() => {
        setRevealedCount((prev) => prev + 1);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isLoading, revealedCount, responses.length]);

  const allRevealed = revealedCount >= responses.length;

  // Calculate skill summary
  const skillSummary = {};
  responses.forEach((response) => {
    if (response.skills) {
      response.skills.forEach((skill) => {
        if (!skillSummary[skill.name]) {
          skillSummary[skill.name] = { demonstrated: 0, total: 0 };
        }
        skillSummary[skill.name].total++;
        if (skill.status === 'demonstrated') {
          skillSummary[skill.name].demonstrated++;
        }
      });
    }
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-950">
        {/* Minimal Header */}
        <header className="sticky top-0 z-50 bg-white/90 dark:bg-gray-900/90 backdrop-blur-md border-b border-gray-100 dark:border-gray-700">
          <div className="max-w-5xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                  {t('practice.step3of3')}
                </span>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {t('practice.reviewLabel')}
                </span>
              </div>
              <ThemeToggle />
            </div>
            {/* Progress bar */}
            <div className="mt-3 h-1 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
              <div className="h-full w-full bg-gray-900 dark:bg-white rounded-full transition-all duration-500" />
            </div>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-16">
          <ThinkingIndicator message={t('practice.analyzingResponses')} />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-950">
      {/* Minimal Header */}
      <header className="sticky top-0 z-50 bg-white/90 dark:bg-gray-900/90 backdrop-blur-md border-b border-gray-100 dark:border-gray-700">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                Step 3 of 3
              </span>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Feedback
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Link href="/student/dashboard">
                <Button variant="ghost" size="sm">
                  {t('practice.backToDashboard')}
                </Button>
              </Link>
              <ThemeToggle />
            </div>
          </div>
          {/* Progress bar */}
          <div className="mt-3 h-1 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
            <div className="h-full w-full bg-gray-900 dark:bg-white rounded-full transition-all duration-500" />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Overall Score Card */}
        <Card gradient elevated className="mb-8 text-center">
          <div className="py-4">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              {t('practice.practiceComplete')}
            </h1>
            <div className="flex justify-center mb-6">
              <ScoreCircle score={overall_score} size="lg" />
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {t('practice.heresFeedback')}
            </p>

            {/* Question Status Summary */}
            <div className="flex justify-center gap-4 mb-6">
              {status_counts.correct > 0 && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-green-100 dark:bg-green-900/30 rounded-full">
                  <span className="w-2 h-2 rounded-full bg-green-500" />
                  <span className="text-sm font-medium text-green-700 dark:text-green-400">
                    {status_counts.correct} {t('practice.correct')}
                  </span>
                </div>
              )}
              {status_counts.incorrect > 0 && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-red-100 dark:bg-red-900/30 rounded-full">
                  <span className="w-2 h-2 rounded-full bg-red-500" />
                  <span className="text-sm font-medium text-red-700 dark:text-red-400">
                    {status_counts.incorrect} {t('practice.incorrect')}
                  </span>
                </div>
              )}
              {status_counts.pending > 0 && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-100 dark:bg-amber-900/30 rounded-full">
                  <span className="w-2 h-2 rounded-full bg-amber-500" />
                  <span className="text-sm font-medium text-amber-700 dark:text-amber-400">
                    {status_counts.pending} {t('practice.pendingReview')}
                  </span>
                </div>
              )}
            </div>

            {/* Skill Summary */}
            {Object.keys(skillSummary).length > 0 && (
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                <h2 className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-4">
                  {t('practice.skillsOverview')}
                </h2>
                <div className="flex flex-wrap justify-center gap-3">
                  {Object.entries(skillSummary).map(([name, data]) => (
                    <div
                      key={name}
                      className={cn(
                        'px-4 py-2 rounded-lg border',
                        data.demonstrated === data.total
                          ? 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                          : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700'
                      )}
                    >
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {name}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {data.demonstrated}/{data.total} {t('practice.demonstrated')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Feedback Panels */}
        <div className="space-y-6 mb-8">
          {responses.map((response, index) => (
            <div
              key={response.id}
              className={cn(
                'transition-all duration-500 ease-out',
                index < revealedCount
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-4'
              )}
            >
              <FeedbackPanel
                questionNumber={index + 1}
                questionPrompt={response.question_prompt}
                questionType={response.question_type}
                studentResponse={response.content}
                score={response.score}
                maxScore={response.max_score}
                status={response.status}
                feedback={response.feedback}
                thinking={response.thinking}
                skills={response.skills}
                // MCQ-specific props
                options={response.options}
                correctAnswer={response.correct_answer}
                studentAnswer={response.student_answer}
                isCorrect={response.is_correct}
                explanation={response.explanation}
                // Short answer/essay props
                modelAnswer={response.model_answer}
                rubric={response.rubric}
                // Table completion props
                placements={response.placements}
              />
            </div>
          ))}
        </div>

        {/* Continue Button */}
        {allRevealed && (
          <div
            className={cn(
              'text-center transition-all duration-500',
              allRevealed ? 'opacity-100' : 'opacity-0'
            )}
          >
            {has_practice_exercises ? (
              <Link href={`/student/practice/${session.id}/exercises`}>
                <Button variant="primary" size="lg">
                  {t('practice.continueToPractice')}
                  <svg
                    className="ml-2 w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </Button>
              </Link>
            ) : (
              <Link href="/student/dashboard">
                <Button variant="primary" size="lg">
                  {t('practice.backToDashboard')}
                </Button>
              </Link>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
